import React, { useState } from 'react';
import { Tab } from 'semantic-ui-react';
import { BarChart3, Table } from 'lucide-react';
import { useBloodTestInfo, useBloodTestData } from '../context/BloodTestDataContext';
import SubcategoryPanel from './SubcategoryPanel';
import SubcategoryTablePanel from './SubcategoryTablePanel';

export default function CategoryTabs() {
  const { categories, subcategories } = useBloodTestInfo();
  const { selectedPatientId, getPatientResultsByCategory } = useBloodTestData();
  const [viewMode, setViewMode] = useState('chart'); // 'chart' or 'table'

  if (!selectedPatientId) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
        <p className="text-gray-500">Please select a patient to view test results</p>
      </div>
    );
  }

  const patientResults = getPatientResultsByCategory(selectedPatientId);

  // Only show categories that have results for this patient
  const categoriesWithResults = categories.filter(category =>
    patientResults[category] && Object.keys(patientResults[category]).length > 0
  );

  if (categoriesWithResults.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
        <p className="text-gray-500">No test results available for this patient</p>
      </div>
    );
  }

  const panes = categoriesWithResults.map(category => ({
    menuItem: category,
    render: () => {
      // Sort subcategories by number of charts (descending)
      const sortedSubcategories = (subcategories[category] || [])
        .map(subcategory => ({
          name: subcategory,
          results: patientResults[category]?.[subcategory] || {},
          count: Object.keys(patientResults[category]?.[subcategory] || {}).length
        }))
        .filter(subcat => subcat.count > 0)
        .sort((a, b) => b.count - a.count);

      return (
        <Tab.Pane key={category}>
          {sortedSubcategories.map(subcat => (
            viewMode === 'chart' ? (
              <SubcategoryPanel
                key={subcat.name}
                category={category}
                subcategory={subcat.name}
                results={subcat.results}
              />
            ) : (
              <SubcategoryTablePanel
                key={subcat.name}
                category={category}
                subcategory={subcat.name}
                results={subcat.results}
              />
            )
          ))}
        </Tab.Pane>
      );
    }
  }));

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      {/* View Toggle */}
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          Test Results Over Time
        </h3>
        <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('chart')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
              viewMode === 'chart'
                ? 'bg-white shadow-sm text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <BarChart3 className="h-4 w-4" />
            <span className="text-sm font-medium">Charts</span>
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
              viewMode === 'table'
                ? 'bg-white shadow-sm text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Table className="h-4 w-4" />
            <span className="text-sm font-medium">Table</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <Tab
        panes={panes}
        menu={{ secondary: true, pointing: true }}
      />
    </div>
  );
}
